import { css } from '@emotion/react';
import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { config } from '@config/pendo.config.json';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import { mobile } from 'styles/global_defaults/media-queries';
import { threeQuartersSpacing } from 'styles/global_defaults/scaffolding';

import { ReactComponent as ProgramEngagementReportingGraphic } from 'styles/icons/program-engagement-reporting.svg';
import { ReactComponent as PulseChecksReportingGraphic } from 'styles/icons/session-pulse-checks-reporting.svg';
import { ReactComponent as DevelopmentGoalsReportingGraphic } from 'styles/icons/development-goals-reporting.svg';
import { ReactComponent as ParticipantProgramProfilesReportingGraphic } from 'styles/icons/participant-program-profiles-reporting.svg';
import { ReactComponent as ProgramReflectionsReportingGraphic } from 'styles/icons/program-reflections-reporting.svg';

import DataDownloadCard from './data-download-card';
import {
  ProgramEngagementPopover,
  PulseChecksPopover,
  DevelopmentGoalsPopover,
  ParticipantProgramProfilesPopover,
  ProgramReflectionsPopover,
} from './data-download-popovers';

const reportingStyles = css`
  .download-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${threeQuartersSpacing}px;

    ${mobile(css`
      grid-template-columns: 1fr;
    `)}
  }
`;

const CARDS_CONFIG = [
  {
    key: 'PROGRAM_ENGAGEMENT',
    reportType: 'program_engagement',
    pendoKey: 'downloadProgramEngagementReport',
    Graphic: ProgramEngagementReportingGraphic,
    Popover: ProgramEngagementPopover,
    graphicProps: { height: 75, width: 110 },
  }, {
    key: 'PULSE_CHECKS',
    reportType: 'pulse_check',
    pendoKey: 'downloadPulseChecksReport',
    Graphic: PulseChecksReportingGraphic,
    Popover: PulseChecksPopover,
    graphicProps: { height: 80, width: 110 },
  }, {
    key: 'DEVELEOPMENT_GOALS',
    reportType: 'goals',
    pendoKey: 'downloadDevelopmentGoalsReport',
    Graphic: DevelopmentGoalsReportingGraphic,
    Popover: DevelopmentGoalsPopover,
    graphicProps: { height: 75, width: 100 },
  }, {
    key: 'PARTICIPANT_PROGRAM_PROFILES',
    reportType: 'program_profile',
    pendoKey: 'downloadParticipantProgramProfilesReport',
    Graphic: ParticipantProgramProfilesReportingGraphic,
    Popover: ParticipantProgramProfilesPopover,
    graphicProps: { height: 85, width: 100 },
  }, {
    key: 'PROGRAM_REFLECTIONS',
    reportType: 'program_reflections',
    pendoKey: 'downloadProgramReflectionsReport',
    Graphic: ProgramReflectionsReportingGraphic,
    Popover: ProgramReflectionsPopover,
    graphicProps: { height: 85, width: 100 },
  },
];

const Reporting = () => {
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const mentoringProgramAliases = useSelector((state: any) => getMentoringProgramAliases(state, mentoringProgram?.id));

  const downloadCards = useMemo(() => CARDS_CONFIG.map(({
    key,
    reportType,
    pendoKey,
    Graphic,
    Popover,
    graphicProps,
  }) => ({
    title: t.MENTORING_PROGRAMS.REPORTING[key]?.() ?? key,
    description: t.MENTORING_PROGRAMS.REPORTING[`${key}_DESCRIPTION`]?.(mentoringProgramAliases) ?? `${key}_DESCRIPTION`,
    ariaLabel: t.MENTORING_PROGRAMS.REPORTING[`${key}_ARIA_LABEL`]?.() ?? `${key}_ARIA_LABEL`,
    downloadUrl: `/mentorship_programs/${mentoringProgram?.id}/download_report?report_type=${reportType}`,
    dataQa: config.pendo.athena.mentorshipProgram.reporting[pendoKey],
    graphic: <Graphic {...graphicProps} />,
    popoverContent: <Popover aliases={mentoringProgramAliases} />,
  })), [mentoringProgram?.id, mentoringProgramAliases]);

  return (
    <div css={reportingStyles}>
      <div className='heading-3 mb-4'>
        {t.MENTORING_PROGRAMS.REPORTING.DATA_DOWNLOADS()}
        <NvTooltip text={t.MENTORING_PROGRAMS.REPORTING.DATA_DOWNLOADS_TOOLTIP()} placement='top'>
          <NvIcon icon='info' size='smallest' className='d-inline gray-3 ml-1' />
        </NvTooltip>
      </div>
      <div className='download-grid'>
        {downloadCards.map((card) => (
          <DataDownloadCard
            key={card.dataQa}
            {...card}
          />
        ))}
      </div>
    </div>
  );
};

export default Reporting;
